import { useState, useEffect } from 'react'
import {
  LBSheet,
  LBSheetContent,
  LBSheetHeader,
  LBSheetTitle,
  LBSheetDescription,
  LBJiraStatusBadge,
  LBSeparator,
  LBScrollArea,
  LBAlertCircleIcon,
  LBTooltip,
  LBTooltipContent,
  LBTooltipTrigger
} from '@linearb/buzz'
import { searchFeatureRolloutTicket } from '../apis/jira'
import type { JiraFeatureRolloutTicket } from '../types'
import {
  JIRA_BROWSE,
  getStatusVariant,
  RichText,
  SectionTitle,
  FieldRow,
  JiraLink,
  UserField,
  BadgeList,
  ScrollableBlock,
  LoadingSkeleton,
  formatDate
} from './jira-shared'

interface JiraFeatureRolloutSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  flagName: string
}

const FR_BOARD_URL =
  'https://linearb.atlassian.net/jira/software/projects/FR/board'

const JiraFeatureRolloutSheet: React.FC<JiraFeatureRolloutSheetProps> = ({
  open,
  onOpenChange,
  flagName
}) => {
  const [ticket, setTicket] = useState<JiraFeatureRolloutTicket | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!open || !flagName) {
      return
    }

    let cancelled = false

    const fetchTicket = async () => {
      setLoading(true)
      setError(false)
      setTicket(null)

      try {
        const { data } = await searchFeatureRolloutTicket(flagName)
        if (!cancelled) {
          setTicket(data)
        }
      } catch {
        if (!cancelled) {
          setError(true)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchTicket()
    return () => {
      cancelled = true
    }
  }, [open, flagName])

  return (
    <LBSheet open={open} onOpenChange={onOpenChange}>
      <LBSheetContent
        side='right'
        className='w-full sm:w-[750px] sm:max-w-[750px] p-0 flex flex-col'
      >
        <LBSheetHeader className='px-6 pt-6 pb-4'>
          <LBSheetTitle className='text-base'>
            Feature Rollout Ticket
          </LBSheetTitle>
          <LBSheetDescription className='text-xs font-mono text-muted-foreground'>
            {flagName}
          </LBSheetDescription>
        </LBSheetHeader>

        <LBScrollArea className='flex-1 px-6 pb-6'>
          {loading && <LoadingSkeleton />}
          {!loading && error && <ErrorState />}
          {!loading && !error && !ticket && (
            <NoMatchState flagName={flagName} />
          )}
          {!loading && !error && ticket && <TicketContent ticket={ticket} />}
        </LBScrollArea>
      </LBSheetContent>
    </LBSheet>
  )
}

const ErrorState: React.FC = () => (
  <div className='flex flex-col items-center justify-center py-12 text-center'>
    <LBAlertCircleIcon className='h-8 w-8 text-muted-foreground mb-3' />
    <p className='text-sm text-muted-foreground mb-3'>
      Could not connect to Jira. Please try again later.
    </p>
    <JiraLink href={FR_BOARD_URL}>Open FR Board</JiraLink>
  </div>
)

const NoMatchState: React.FC<{ flagName: string }> = ({ flagName }) => (
  <div className='flex flex-col items-center justify-center py-12 text-center'>
    <p className='text-sm text-muted-foreground mb-1'>No FR ticket found for</p>
    <p className='text-sm font-mono text-foreground bg-muted rounded px-3 py-1.5 mb-4'>
      {flagName}
    </p>
    <JiraLink href={FR_BOARD_URL}>Browse FR Board</JiraLink>
  </div>
)

const TicketContent: React.FC<{ ticket: JiraFeatureRolloutTicket }> = ({
  ticket
}) => {
  const jiraUrl = `${JIRA_BROWSE}/${ticket.key}`

  return (
    <div className='space-y-5 pt-2'>
      {/* Header */}
      <div className='bg-muted rounded-lg p-4 -mx-1'>
        <div className='flex items-center gap-2 mb-2'>
          <JiraLink href={jiraUrl}>
            <span className='text-sm font-bold'>{ticket.key}</span>
          </JiraLink>
          {ticket.status?.name && (
            <LBJiraStatusBadge variant={getStatusVariant(ticket.status)}>
              {ticket.status.name}
            </LBJiraStatusBadge>
          )}
        </div>
        <p className='text-sm text-foreground leading-relaxed font-medium'>
          {ticket.summary}
        </p>
      </div>

      {/* Description */}
      {ticket.description && (
        <>
          <LBSeparator />
          <div>
            <SectionTitle>Description</SectionTitle>
            <LBScrollArea className='max-h-40'>
              <div className='text-xs text-foreground bg-muted rounded-md p-3 leading-relaxed'>
                <RichText text={ticket.description} />
              </div>
            </LBScrollArea>
          </div>
        </>
      )}

      <LBSeparator />

      {/* Overview */}
      <div className='space-y-1'>
        <SectionTitle>Overview</SectionTitle>
        <FieldRow label='Feature Tier'>
          {ticket.feature_tier?.value || '-'}
        </FieldRow>
        <FieldRow label='Investment Type'>
          {ticket.investment_type?.value || '-'}
        </FieldRow>
        <FieldRow label='Target Launch'>
          {formatDate(ticket.target_launch)}
        </FieldRow>
        <FieldRow label='Target Status'>
          {ticket.target_status_at_launch?.value || '-'}
        </FieldRow>
      </div>

      <LBSeparator />

      {/* People */}
      <div className='space-y-1'>
        <SectionTitle>People</SectionTitle>
        <UserField label='Assignee' user={ticket.assignee} />
        <UserField label='Product Manager' user={ticket.product_manager} />
      </div>

      <LBSeparator />

      {/* Timeline */}
      <div className='space-y-1'>
        <SectionTitle>Timeline</SectionTitle>
        <FieldRow label='Closed Beta'>
          {formatDate(ticket.closed_beta_estimated)}
        </FieldRow>
        <FieldRow label='Open Beta'>
          {formatDate(ticket.open_beta_estimated)}
        </FieldRow>
        <FieldRow label='GA'>{formatDate(ticket.ga_estimated)}</FieldRow>
      </div>

      <LBSeparator />

      {/* Details */}
      <div className='space-y-1'>
        <SectionTitle>Details</SectionTitle>
        {ticket.package_license && ticket.package_license.length > 0 && (
          <BadgeList
            label='Package'
            items={ticket.package_license}
            variant='secondary'
          />
        )}
        {ticket.relevant_integration &&
          ticket.relevant_integration.length > 0 && (
            <BadgeList
              label='Integration'
              items={ticket.relevant_integration}
              variant='outline'
            />
          )}
        {ticket.feature_brief_link && (
          <FieldRow label='Brief'>
            {ticket.feature_brief_link.startsWith('http') ? (
              <JiraLink href={ticket.feature_brief_link}>
                <span className='text-sm truncate max-w-[500px]'>
                  {ticket.feature_brief_link}
                </span>
              </JiraLink>
            ) : (
              <span className='text-sm text-muted-foreground'>
                {ticket.feature_brief_link}
              </span>
            )}
          </FieldRow>
        )}
      </div>

      {/* Scrollable text blocks */}
      {(
        [
          ['Orgs in Closed Beta', ticket.orgs_in_closed_beta],
          ['Known Limitations', ticket.known_limitations],
          ['Rollout Entry Criteria', ticket.rollout_entry_criteria],
          ['Additional Resources', ticket.additional_resources],
          ['Documentation', ticket.documentation]
        ] as const
      ).map(([title, content]) =>
        content ? (
          <ScrollableBlock key={title} title={title} content={content} />
        ) : null
      )}

      {/* Subtasks */}
      {ticket.subtasks && ticket.subtasks.length > 0 && (
        <>
          <LBSeparator />
          <div>
            <SectionTitle>Subtasks ({ticket.subtasks.length})</SectionTitle>
            <div className='space-y-0.5 bg-muted rounded-lg p-2'>
              {ticket.subtasks.map(sub => (
                <div
                  key={sub.key}
                  className='flex items-center justify-between py-2 px-2 rounded hover:bg-background transition-colors'
                >
                  <div className='flex items-center gap-2 min-w-0'>
                    <a
                      href={`${JIRA_BROWSE}/${sub.key}`}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-xs text-blue-600 hover:text-blue-800 shrink-0 font-semibold transition-colors'
                    >
                      {sub.key}
                    </a>
                    <LBTooltip>
                      <LBTooltipTrigger asChild>
                        <span className='text-sm text-foreground truncate'>
                          {sub.summary}
                        </span>
                      </LBTooltipTrigger>
                      <LBTooltipContent>
                        <p>{sub.summary}</p>
                      </LBTooltipContent>
                    </LBTooltip>
                  </div>
                  {sub.status?.name && (
                    <LBJiraStatusBadge
                      variant={getStatusVariant(sub.status)}
                      className='shrink-0 ml-2'
                    >
                      {sub.status.name}
                    </LBJiraStatusBadge>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default JiraFeatureRolloutSheet
